import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import FullPageSpinner from "../layout/FullPageSpinner";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import {
  fetchAllOrders,
  fetchOrderById,
  fetchAcademicYears,
  fetchDtdcTracking,
  fetchDtdcLabel,
  generateDtdcOrder,
  fetchInvoicePdfBlob,
  fetchBulkInvoicePdfBlob,
  fetchActiveDtdcConfigs,
  generateOrderSlipsPdf,
  bulkUpdateDeliveryStatus,
  fetchOrderStatusList
} from "../../actions/product";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../services/helper";
import JsBarcode from "jsbarcode";
import { utils, writeFile } from "xlsx";
import DtdcConfigManager from "./DtdcConfigManager";
import UpdateOrderAddressModal from "./UpdateOrderAddressModal";
import { ROLES } from "../../utils/constants";

// Inline Barcode Renderer for Table Rows
const TableBarcode = ({ value }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current && value) {
      try {
        JsBarcode(canvasRef.current, String(value), {
          format: "CODE128",
          width: 1.1,
          height: 28,
          displayValue: false,
          margin: 0
        });
      } catch (e) {
        console.error("Barcode render error:", e);
      }
    }
  }, [value]);

  if (!value) return <span className="text-muted small">-</span>;

  return (
    <div className="d-flex flex-column align-items-center" title={`Tracking ID: ${value}`}>
      <canvas ref={canvasRef} style={{ maxWidth: '130px', height: '24px' }} />
      <span className="font-monospace text-dark fw-bold" style={{ fontSize: '11px', marginTop: '2px' }}>
        {value}
      </span>
    </div>
  );
};

// DTDC Tracking Details Modal
const TrackingDetailsModal = ({ show, onHide, trackingData }) => {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title className="fs-6 fw-bold">
          <i className="bi bi-truck me-2"></i>DTDC Tracking Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {trackingData && trackingData.status === "SUCCESS" ? (
          <div>
            <div className="mb-4 p-3 bg-light rounded border">
              <div className="row g-3">
                <div className="col-md-6">
                  <p className="mb-1 text-muted small">AWB Number</p>
                  <p className="fw-bold mb-2 font-monospace fs-6">{trackingData.trackHeader?.strcnno}</p>
                  <p className="mb-1 text-muted small">Current Status</p>
                  <span className="badge bg-primary text-uppercase px-2 py-1">
                    {trackingData.trackHeader?.strStatus}
                  </span>
                </div>
                <div className="col-md-6">
                  <p className="mb-1 text-muted small">Origin</p>
                  <p className="fw-bold mb-2">{trackingData.trackHeader?.strOrigin || '-'}</p>
                  <p className="mb-1 text-muted small">Destination</p>
                  <p className="fw-bold mb-0">{trackingData.trackHeader?.strDest || '-'}</p>
                </div>
              </div>
            </div>

            <h6 className="fw-bold mb-3">Shipment Activity & History</h6>
            <div className="table-responsive border rounded">
              <Table hover size="sm" className="mb-0">
                <thead className="table-light small">
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Location</th>
                    <th>Activity</th>
                  </tr>
                </thead>
                <tbody className="small">
                  {trackingData.trackDetails && trackingData.trackDetails.length > 0 ? (
                    trackingData.trackDetails.map((detail, index) => (
                      <tr key={index}>
                        <td>{detail.strActionDate}</td>
                        <td>{detail.strActionTime}</td>
                        <td>{detail.strLocation}</td>
                        <td className="fw-semibold">{detail.strAction}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-3 text-muted">No tracking timeline history available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-box-seam fs-1 d-block mb-2 text-secondary"></i>
            {trackingData?.message || "No tracking information found for this DTDC consignment."}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Allowed statuses representing Shipped to Delivery lifecycle
const SHIPPED_LIFECYCLE_STATUSES = [
  "IN_PROGRESS",
  "ARRIVED_AT_WAREHOUSE",
  "PACKED",
  "SHIPPED",
  "IN_TRANSIT",
  "OUT_FOR_DELIVERY",
  "RTO"
];

// Statuses eligible for DTDC shipment generation
const DTDC_ELIGIBLE_STATUSES = [
  "IN_PROGRESS",
  "PACKED",
  "ARRIVED_AT_WAREHOUSE"
];

const DtdcShipmentOrders = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperadmin = user?.role === ROLES.SUPER_ADMIN;

  const hasPermission = (permission) => {
    if (user?.role === ROLES.SUPER_ADMIN) return true;
    return user?.permissions?.[permission] === true;
  };
  const canUpdateStatus = hasPermission('order_status_update') || isSuperadmin || user?.role === 'admin' || user?.role === 'store_admin';

  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "IN_PROGRESS";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateFilterBy, setDateFilterBy] = useState("updatedAt");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [academicYears, setAcademicYears] = useState([]);
  const [currentYear, setCurrentYear] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState([]);

  // Bulk status update states
  const [orderStatusList, setOrderStatusList] = useState([]);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState("");
  const [bulkStatusNote, setBulkStatusNote] = useState("");

  // Selected orders eligible for DTDC generation (In-Progress, Packed, or At Warehouse without tracking ID)
  const selectedDtdcEligibleOrders = useMemo(() => {
    return selectedOrders
      .map((id) => orders.find((o) => o.id === id))
      .filter((o) => o && DTDC_ELIGIBLE_STATUSES.includes((o.status || "").toUpperCase()) && !o.trackingId);
  }, [selectedOrders, orders]);

  // Modals & previews
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [fetchingTracking, setFetchingTracking] = useState(false);
  const [selectedViewOrder, setSelectedViewOrder] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [pdfPreview, setPdfPreview] = useState({
    show: false,
    url: "",
    blob: null,
    fileName: ""
  });

  // DTDC Config states
  const [activeDtdcConfigs, setActiveDtdcConfigs] = useState([]);
  const [showConfigManager, setShowConfigManager] = useState(false);
  const [shipPromptModal, setShipPromptModal] = useState({
    show: false,
    order: null,
    orderIds: [],
    customerCode: ""
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [years, dtdcList, statuses] = await Promise.all([
        fetchAcademicYears(),
        fetchActiveDtdcConfigs().catch(() => []),
        fetchOrderStatusList().catch(() => []),
      ]);
      if (years && Array.isArray(years)) {
        setAcademicYears(years);
        const current = years.find((y) => y.is_current === true) || years[0];
        if (current) setCurrentYear(current.year);
      }
      if (Array.isArray(dtdcList)) {
        setActiveDtdcConfigs(dtdcList);
      }
      if (Array.isArray(statuses) && statuses.length > 0) {
        setOrderStatusList(statuses);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getShippedOrders = useCallback(async () => {
    setLoading(true);
    try {
      // If a specific status filter is chosen by user, use it. Otherwise, query all or SHIPPED
      const filterToApply = statusFilter || "";

      const response = await fetchAllOrders(
        currentPage,
        itemsPerPage,
        searchTerm,
        startDate,
        endDate,
        filterToApply,
        "PAID", // paymentstatusFilter
        "SETTLED", // settlementStatus
        false, // isVendor
        dateFilterBy === "updatedAt" ? "order.updatedAt" : "order.createdAt",
        "DESC",
        currentYear,
        "false",
        "non-shoes",
        "",
        "",
        dateFilterBy
      );

      let fetchedList = response.orders || [];

      // Filter in frontend to ensure we focus on orders with tracking or in shipped-delivery sequence
      if (!statusFilter) {
        fetchedList = fetchedList.filter(o => 
          o.trackingId || SHIPPED_LIFECYCLE_STATUSES.includes(o.status)
        );
      }

      setOrders(fetchedList);
      setTotalPages(response.totalPages || 1);
      setTotalItems(response.total || fetchedList.length);
    } catch (error) {
      console.error("Error fetching shipped orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, startDate, endDate, statusFilter, currentYear, dateFilterBy]);

  useEffect(() => {
    getShippedOrders();
  }, [getShippedOrders]);

  const handleSelectOrder = (id) => {
    setSelectedOrders(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      return [...prev, id];
    });
  };

  const toggleSelectAll = () => {
    const pageIds = orders.map(o => o.id);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selectedOrders.includes(id));
    if (allOnPageSelected) {
      setSelectedOrders(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedOrders(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleClearSelection = () => {
    setSelectedOrders([]);
  };

  // Bulk update delivery status for selected orders
  const handleBulkStatusUpdate = async () => {
    if (!canUpdateStatus) {
      toast.error("You do not have permission to update order status.");
      return;
    }
    if (selectedOrders.length === 0) {
      toast.warning("Please select at least one order to update.");
      return;
    }
    if (!bulkTargetStatus) {
      toast.warning("Please choose a delivery status.");
      return;
    }

    setLoading(true);
    try {
      const transformedData = {
        updates: selectedOrders.map((orderId) => ({
          orderId: orderId,
          status: bulkTargetStatus,
          note: bulkStatusNote || undefined,
        })),
      };

      const response = await bulkUpdateDeliveryStatus(transformedData);
      if (response && response.success !== false) {
        if (bulkTargetStatus === "PACKED") {
          toast.success(
            response.message ||
              `Updated ${selectedOrders.length} order(s) to PACKED. DTDC consignments are being generated in the background for paid orders!`
          );
        } else {
          toast.success(
            response.message ||
              `Successfully updated delivery status to ${bulkTargetStatus.replace(/_/g, " ")} for ${selectedOrders.length} order(s)!`
          );
        }
        setShowBulkStatusModal(false);
        setBulkTargetStatus("");
        setBulkStatusNote("");
        setSelectedOrders([]);
        getShippedOrders();
      } else {
        toast.error(response?.message || "Bulk status update partially failed.");
      }
    } catch (error) {
      console.error("Error in bulk status update:", error);
      toast.error(error.response?.data?.message || "Failed to update bulk status.");
    } finally {
      setLoading(false);
    }
  };

  // Bulk generate DTDC consignments for selected orders (enforced 10 restriction)
  const handleBulkGenerateDtdc = async (orderIds, chosenCustomerCode) => {
    if (!orderIds || orderIds.length === 0) {
      toast.warning("Please select at least one order to generate DTDC consignments.");
      return;
    }

    let inputIds = orderIds;
    if (inputIds.length > 10) {
      toast.info("DTDC generation is restricted to a maximum of 10 orders per batch to prevent carrier API timeouts. Processing first 10 orders...");
      inputIds = inputIds.slice(0, 10);
    }

    const selectedObjs = orders.filter(o => inputIds.includes(o.id));
    const eligibleIds = selectedObjs
      .filter(o => DTDC_ELIGIBLE_STATUSES.includes((o.status || "").toUpperCase()) && !o.trackingId)
      .map(o => o.id);

    if (eligibleIds.length === 0) {
      toast.info("Selected orders do not have an eligible status (In Progress, Packed, or At Warehouse) or already have DTDC tracking generated.");
      return;
    }

    const idsToProcess = eligibleIds.slice(0, 10);

    // If multiple customer codes exist and no code chosen yet, prompt user
    if (!chosenCustomerCode && activeDtdcConfigs.length > 1) {
      const defaultCode = activeDtdcConfigs.find(c => c.isDefault)?.customerCode || activeDtdcConfigs[0]?.customerCode || "";
      setShipPromptModal({
        show: true,
        order: null,
        orderIds: idsToProcess,
        customerCode: defaultCode
      });
      return;
    }

    const codeToUse = chosenCustomerCode || (activeDtdcConfigs.find(c => c.isDefault)?.customerCode || activeDtdcConfigs[0]?.customerCode || undefined);
    setShipPromptModal({ show: false, order: null, orderIds: [], customerCode: "" });

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of idsToProcess) {
      try {
        const res = await generateDtdcOrder(id, codeToUse, false);
        if (res?.status || res?.success || res?.trackingId) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        console.error(`Error generating DTDC order for #${id}:`, err);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully generated DTDC consignments for ${successCount} order(s)!`);
      setSelectedOrders(prev => prev.filter(id => !idsToProcess.includes(id)));
      getShippedOrders();
    }
    if (failCount > 0) {
      toast.error(`Failed to generate DTDC consignments for ${failCount} order(s). Check order details.`);
    }
    setLoading(false);
  };

  // Open Order Details Modal
  const handleViewOrderDetails = async (order) => {
    try {
      const fullOrder = await fetchOrderById(order.id);
      setSelectedViewOrder(fullOrder || order);
    } catch (err) {
      console.error("Error loading order details:", err);
      setSelectedViewOrder(order);
    }
  };

  // Move / Ship Order to DTDC and immediately show Tax Invoice with tracking barcode
  const handleShipOrder = async (order, chosenCustomerCode) => {
    if (!order) return;
    if (!DTDC_ELIGIBLE_STATUSES.includes((order.status || "").toUpperCase()) || order.trackingId) {
      toast.warning("This order is already shipped or already has a DTDC tracking ID.");
      return;
    }

    // If customer code was not explicitly provided and multiple customer codes exist, prompt user
    if (!chosenCustomerCode && activeDtdcConfigs.length > 1) {
      const defaultCode = activeDtdcConfigs.find(c => c.isDefault)?.customerCode || activeDtdcConfigs[0]?.customerCode || "";
      setShipPromptModal({
        show: true,
        order: order,
        customerCode: defaultCode
      });
      return;
    }

    const codeToUse = chosenCustomerCode || (activeDtdcConfigs.find(c => c.isDefault)?.customerCode || activeDtdcConfigs[0]?.customerCode || undefined);
    setShipPromptModal({ show: false, order: null, customerCode: "" });

    setLoading(true);
    try {
      await generateDtdcOrder(order.id, codeToUse);
      const updatedOrder = await fetchOrderById(order.id);
      if (updatedOrder) {
        setOrders(prev => prev.map(o => o.id === order.id ? updatedOrder : o));
        setSelectedViewOrder(updatedOrder);

        // Immediately show Tax Invoice preview with tracking barcode
        try {
          const blob = await fetchInvoicePdfBlob(order.id);
          const url = URL.createObjectURL(blob);
          setPdfPreview({
            show: true,
            url: url,
            blob: blob,
            fileName: `Tax_Invoice_${updatedOrder.orderId || updatedOrder.id}.pdf`
          });
          toast.success(`Order #${updatedOrder.orderId || updatedOrder.id} dispatched! Tax Invoice preview with tracking barcode is ready.`);
        } catch (pdfErr) {
          console.error("Error generating invoice preview:", pdfErr);
        }
      }
    } catch (error) {
      console.error("Error shipping order via DTDC:", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate and preview Tax Invoice first from backend, allowing user to view and download
  const handlePrintTaxInvoice = async (orderOrIds) => {
    setLoading(true);
    try {
      if (Array.isArray(orderOrIds)) {
        if (orderOrIds.length === 0) {
          toast.warning("Please select at least one order to generate invoice.");
          setLoading(false);
          return;
        }
        const blob = await fetchBulkInvoicePdfBlob(orderOrIds);
        const url = URL.createObjectURL(blob);
        setPdfPreview({
          show: true,
          url: url,
          blob: blob,
          fileName: `Tax_Invoices_Bulk_${orderOrIds.length}_Orders.pdf`
        });
        toast.success(`Tax Invoices preview ready for ${orderOrIds.length} orders!`);
      } else {
        const orderId = orderOrIds.id;
        const blob = await fetchInvoicePdfBlob(orderId);
        const url = URL.createObjectURL(blob);
        setPdfPreview({
          show: true,
          url: url,
          blob: blob,
          fileName: `Tax_Invoice_${orderId}.pdf`
        });
        toast.success(`Tax Invoice preview ready for Order #${orderId}`);
      }
    } catch (error) {
      console.error("Error generating invoice PDF preview:", error);
      toast.error("Failed to generate Tax Invoice from server");
    } finally {
      setLoading(false);
    }
  };

  // Generate and preview Order Slip (with house colors applied)
  const handlePrintOrderSlip = async (orderOrIds) => {
    setLoading(true);
    try {
      const orderIds = Array.isArray(orderOrIds)
        ? orderOrIds
        : [orderOrIds.id || orderOrIds];
      if (orderIds.length === 0) {
        toast.warning("Please select at least one order to generate order slips.");
        setLoading(false);
        return;
      }
      const data = await generateOrderSlipsPdf({ orderIds });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfPreview({
        show: true,
        url: url,
        blob: blob,
        fileName: `Order_Slips_${orderIds.length}_Orders.pdf`
      });
      toast.success(`Order slip preview ready for ${orderIds.length} order(s)!`);
    } catch (error) {
      console.error("Error generating order slip:", error);
      toast.error("Failed to generate order slip from server");
    } finally {
      setLoading(false);
    }
  };

  // View Live DTDC Tracking
  const handleViewTracking = async (order) => {
    setFetchingTracking(true);
    try {
      const data = await fetchDtdcTracking(order.id);
      setTrackingData(data);
      setShowTrackingModal(true);
    } catch (error) {
      console.error("Error fetching tracking details:", error);
      toast.error("Failed to fetch tracking details");
    } finally {
      setFetchingTracking(false);
    }
  };

  // Print DTDC Shipping Label
  const handlePrintDtdcLabel = async (orderId) => {
    setLoading(true);
    try {
      const response = await fetchDtdcLabel(orderId, "SHIP_LABEL_A4");
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfPreview({
        show: true,
        url: url,
        blob: blob,
        fileName: `DTDC_Label_${orderId}.pdf`
      });
    } catch (error) {
      console.error("Error printing DTDC label:", error);
      toast.error("Failed to fetch DTDC shipping label");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfPreview.blob && pdfPreview.fileName) {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfPreview.blob);
      link.download = pdfPreview.fileName;
      link.click();
      setPdfPreview(prev => ({ ...prev, show: false }));
    }
  };

  const handleFilterMovedToday = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setDateFilterBy("updatedAt");
    setStatusFilter("IN_PROGRESS");
    setCurrentPage(1);
  };

  const handleFilterTodayAll = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setDateFilterBy("updatedAt");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const handleExportExcel = () => {
    if (orders.length === 0) {
      toast.warning("No orders available to export.");
      return;
    }

    const listToExport = selectedOrders.length > 0
      ? orders.filter(o => selectedOrders.includes(o.id))
      : orders;

    const data = listToExport.map((o, idx) => ({
      "#": idx + 1,
      "InvoiceNo No": o.invoiceNo || "",
      "Order ID": o.orderId || o.id,
      "Tracking ID": o.trackingId || "-",
      "Status": o.status || "-",
      "Parent Name": o.parent?.parentName || o.studentNames || "-",
      "Phone": o.deliveryContact || o.parent?.phoneNumber || "-",
      "Delivery Address": o.deliveryAddress || o.parent?.address || "-",
      "City": o.city || o.parent?.city || "-",
      "State": o.state || o.parent?.state || "-",
      "Pincode": o.pincode || o.parent?.pincode || "-",
      "Shipping Method": o.shippingMethod || "Home",
      "Updated On": o.updatedAt ? new Date(o.updatedAt).toLocaleString() : "-",
      "Created On": o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"
    }));

    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Orders");
    const filename = `DTDC_Orders_${listToExport.length}_${Date.now()}.xlsx`;
    writeFile(wb, filename);
    toast.success(`Exported ${listToExport.length} orders to Excel successfully!`);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setDateFilterBy("updatedAt");
    setStatusFilter("IN_PROGRESS");
    setCurrentPage(1);
    const current = academicYears.find((y) => y.is_current === true) || academicYears[0];
    if (current) setCurrentYear(current.year);
    getShippedOrders();
  };

  return (
    <div className="container-fluid py-4 px-4 bg-light min-vh-100">
      {loading && <FullPageSpinner loading={loading} />}

      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-2 border-bottom">
        <div>
          <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-truck-flatbed text-primary"></i>
            DTDC Shipped & Delivery Tracking Module
          </h4>
          <p className="text-muted small mb-0">
            Track all dispatched orders from shipment to delivery, view live tracking barcodes, and generate sample-format Tax Invoices in bulk or individually.
          </p>
        </div>

        {/* Global Actions */}
        <div className="d-flex gap-2 mt-3 mt-md-0 flex-wrap align-items-center">
          {selectedOrders.length > 0 && (
            <>
              {canUpdateStatus && (
                <Button
                  variant="primary"
                  className="fw-bold shadow-sm d-flex align-items-center gap-2"
                  onClick={() => setShowBulkStatusModal(true)}
                  title="Bulk update delivery status (Packed, Shipped, etc.)"
                >
                  <i className="bi bi-pencil-square"></i>
                  Update Status ({selectedOrders.length})
                </Button>
              )}

              {selectedDtdcEligibleOrders.length > 0 && (
                <Button
                  variant="warning"
                  className="fw-bold shadow-sm d-flex align-items-center gap-2"
                  onClick={() => {
                    if (selectedDtdcEligibleOrders.length > 10) {
                      toast.info("DTDC generation is restricted to a maximum of 10 orders per batch. Processing first 10 orders...");
                    }
                    handleBulkGenerateDtdc(selectedDtdcEligibleOrders.slice(0, 10).map((o) => o.id));
                  }}
                  title="Generate DTDC consignments (Restricted to 10 orders per batch)"
                >
                  <i className="bi bi-truck"></i>
                  Generate DTDC ({selectedDtdcEligibleOrders.length > 10 ? `10 of ${selectedDtdcEligibleOrders.length}` : selectedDtdcEligibleOrders.length})
                  {selectedDtdcEligibleOrders.length > 10 && (
                    <span className="badge bg-dark bg-opacity-75 ms-1" style={{ fontSize: "10px" }}>Max 10</span>
                  )}
                </Button>
              )}

              <Button
                variant="success"
                className="fw-bold shadow-sm d-flex align-items-center gap-2"
                onClick={() => handlePrintTaxInvoice(selectedOrders)}
                title="Print Tax Invoices for all selected orders"
              >
                <i className="bi bi-receipt"></i>
                Print Invoices ({selectedOrders.length})
              </Button>

              <Button
                variant="outline-secondary"
                className="fw-bold shadow-sm d-flex align-items-center gap-2"
                onClick={() => handlePrintOrderSlip(selectedOrders)}
                title="Print Order Slips for all selected orders"
              >
                <i className="bi bi-printer"></i>
                Print Slips ({selectedOrders.length})
              </Button>

              <Button
                variant="outline-success"
                className="fw-bold shadow-sm d-flex align-items-center gap-2"
                onClick={handleExportExcel}
                title="Export selected orders to Excel"
              >
                <i className="bi bi-file-earmark-excel"></i>
                Export Excel ({selectedOrders.length})
              </Button>

              <Button
                variant="outline-danger"
                size="sm"
                className="fw-bold d-flex align-items-center gap-1"
                onClick={handleClearSelection}
                title="Clear selected orders"
              >
                <i className="bi bi-x-circle"></i> Clear
              </Button>
            </>
          )}

          {selectedOrders.length === 0 && (
            <Button
              variant="outline-success"
              className="fw-bold shadow-sm d-flex align-items-center gap-1"
              onClick={handleExportExcel}
              title="Export currently loaded list to Excel"
            >
              <i className="bi bi-file-earmark-excel"></i> Export List
            </Button>
          )}
          {/* DTDC Customer Codes Button - Visible ONLY to Superadmin */}
          {isSuperadmin && (
            <Button
              variant="outline-secondary"
              className="fw-bold shadow-sm d-flex align-items-center gap-1"
              onClick={() => setShowConfigManager(true)}
            >
              <i className="bi bi-gear-wide-connected"></i> DTDC Customer Codes
              {activeDtdcConfigs.length > 0 && (
                <span className="badge bg-secondary ms-1">{activeDtdcConfigs.length}</span>
              )}
            </Button>
          )}
          <Button
            variant="outline-primary"
            className="fw-bold shadow-sm"
            onClick={getShippedOrders}
          >
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            
            {/* Search Input */}
            <div className="col-12 col-md-3">
              <label className="form-label small fw-bold text-muted mb-1">Search</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <i className="bi bi-search"></i>
                </span>
                <Form.Control
                  type="text"
                  placeholder="Order ID, Tracking, USID..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border-start-0 shadow-none"
                />
              </div>
            </div>

            {/* Date Filter Basis: Updated Date vs Created Date */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">
                <i className="bi bi-funnel me-1 text-primary"></i>Filter Date By
              </label>
              <Form.Select
                size="sm"
                value={dateFilterBy}
                onChange={(e) => {
                  setDateFilterBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="shadow-none border-primary fw-semibold"
              >
                <option value="updatedAt">Updated / Moved Date</option>
                <option value="createdAt">Order Placed Date</option>
              </Form.Select>
            </div>

            {/* From Date */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">From Date</label>
              <Form.Control
                type="date"
                size="sm"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="shadow-none"
              />
            </div>

            {/* To Date */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">To Date</label>
              <Form.Control
                type="date"
                size="sm"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="shadow-none"
              />
            </div>

            {/* Status Filter */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">Shipment Status</label>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="shadow-none"
              >
                <option value="IN_PROGRESS">In Progress (Default)</option>
                <option value="ARRIVED_AT_WAREHOUSE">At Warehouse</option>
                <option value="PACKED">Packed</option>
                <option value="SHIPPED">Shipped</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="OUT_FOR_DELIVERY">Out For Delivery</option>
                <option value="RTO">RTO (Returned)</option>
                <option value="">-- All Shipment Statuses --</option>
              </Form.Select>
            </div>

            {/* Reset Button */}
            <div className="col-6 col-md-1 d-flex justify-content-md-end">
              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100 fw-bold"
                onClick={handleResetFilters}
                title="Reset all filters"
              >
                <i className="bi bi-x-circle me-1"></i> Reset
              </Button>
            </div>

          </div>

          {/* Quick Filter Presets */}
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-2 border-top">
            <span className="small fw-bold text-muted me-1">
              <i className="bi bi-lightning-charge text-warning me-1"></i>Quick Filters:
            </span>
            <Button
              variant={statusFilter === "IN_PROGRESS" && dateFilterBy === "updatedAt" && startDate === (new Date().toISOString().split("T")[0]) ? "primary" : "outline-primary"}
              size="sm"
              className="py-1 px-2 fw-semibold"
              onClick={handleFilterMovedToday}
            >
              <i className="bi bi-clock-history me-1"></i>Moved to In-Progress Today
            </Button>
            <Button
              variant={statusFilter === "" && dateFilterBy === "updatedAt" && startDate === (new Date().toISOString().split("T")[0]) ? "secondary" : "outline-secondary"}
              size="sm"
              className="py-1 px-2"
              onClick={handleFilterTodayAll}
            >
              All Updated Today
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="py-1 px-2"
              onClick={() => {
                const y = new Date();
                y.setDate(y.getDate() - 1);
                const yStr = y.toISOString().split("T")[0];
                setStartDate(yStr);
                setEndDate(yStr);
                setDateFilterBy("updatedAt");
                setStatusFilter("IN_PROGRESS");
                setCurrentPage(1);
              }}
            >
              Moved Yesterday
            </Button>
            {(startDate || endDate) && (
              <Button
                variant="link"
                size="sm"
                className="text-danger p-0 ms-2 text-decoration-none small"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setCurrentPage(1);
                }}
              >
                <i className="bi bi-x-circle me-1"></i>Clear Date Range
              </Button>
            )}
            <div className="ms-auto d-flex align-items-center gap-2">
              <span className="badge bg-light text-dark border">
                Academic Year: {currentYear || "All"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table Card */}
      <div className="card shadow-sm border-0 rounded-3 bg-white">
        <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-dark">
              {statusFilter === "IN_PROGRESS"
                ? "In-Progress Orders List"
                : statusFilter
                ? `${statusFilter.replace(/_/g, " ")} Orders List`
                : "All DTDC Orders List"}
            </span>
            <span className="badge bg-primary rounded-pill">{totalItems} Total</span>
            {dateFilterBy === "updatedAt" && startDate && (
              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill">
                <i className="bi bi-arrow-repeat me-1"></i>
                {startDate === endDate ? `Moved on ${startDate}: ${totalItems} orders` : `Moved between ${startDate} and ${endDate}: ${totalItems} orders`}
              </span>
            )}
          </div>

          {selectedOrders.length > 0 && (
            <div className="text-muted small d-flex align-items-center gap-2">
              <span className="badge bg-warning text-dark border">
                <strong>{selectedOrders.length}</strong> selected
              </span>
              {selectedDtdcEligibleOrders.length > 0 && (
                <span className="badge bg-success text-white">
                  <i className="bi bi-truck me-1"></i>
                  {selectedDtdcEligibleOrders.length} Eligible for DTDC
                </span>
              )}
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none small text-muted"
                onClick={() => setSelectedOrders([])}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light small">
              <tr>
                <th className="text-center px-3" style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={orders.length > 0 && orders.every(o => selectedOrders.includes(o.id))}
                    onChange={toggleSelectAll}
                    title="Select all orders on this page"
                  />
                </th>
                <th>Invoice No</th>
                <th>Order ID</th>
                <th>Parent Name</th>
                <th>Student USID(s)</th>
                <th>Student Name(s)</th>
                <th className="text-center" style={{ minWidth: '160px' }}>Tracking Barcode</th>
                <th className="text-end">Amount</th>
                <th>Delivery Status</th>
                <th>Shipment Date</th>
                <th className="text-center" style={{ minWidth: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length > 0 ? (
                orders.map((order) => {
                  return (
                    <tr key={order.id}>
                      <td className="text-center px-3">
                        <Form.Check
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          // disabled={!selectedOrders.includes(order.id)}
                          onChange={() => handleSelectOrder(order.id)}
                          title={"Select order"}
                        />
                      </td>
                      <td className="fw-bold text-dark">{order.invoiceNo || '-'}</td>
                      <td className="fw-bold text-dark">
                        <div>{order.orderId}</div>
                        {order.customerCode && (
                          <span
                            className="badge bg-secondary font-monospace mt-1"
                            style={{ fontSize: "10px", fontWeight: "normal" }}
                            title={`DTDC Customer Code: ${order.customerCode}`}
                          >
                            {order.customerCode}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: '140px' }} title={order.parent?.parentName}>
                          {order.parent?.parentName}
                        </div>
                      </td>
                      <td className="small">{order.studentUSIDs || '-'}</td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '160px' }} title={order.studentNames}>
                          {order.studentNames || '-'}
                        </div>
                      </td>

                      {/* Barcode column for Tracking ID */}
                      <td className="text-center py-2">
                        {order.trackingId ? (
                          <TableBarcode value={order.trackingId} />
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary border">No Tracking</span>
                        )}
                      </td>

                      <td className="text-end fw-bold text-dark">₹{order.totalPrice}</td>
                      <td>
                        <span className={`badge rounded-pill ${
                          order.status === 'DELIVERED' ? 'bg-success' :
                          order.status === 'OUT_FOR_DELIVERY' ? 'bg-primary' :
                          order.status === 'SHIPPED' || order.status === 'IN_TRANSIT' ? 'bg-info text-dark' :
                          order.status === 'PACKED' ? 'bg-secondary' :
                          order.status === 'IN_PROGRESS' ? 'bg-warning text-dark' :
                          'bg-secondary'
                        } text-capitalize px-2 py-1`}>
                          {order.status ? order.status.replace(/_/g, ' ') : 'In Progress'}
                        </span>
                      </td>
                      <td className="small text-muted">{formatDateTime(order.updatedAt || order.createdAt)}</td>

                      {/* Actions */}
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                          
                          {/* View Order Details */}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-circle"
                            onClick={() => handleViewOrderDetails(order)}
                            title="View Order Details & Shipping"
                            style={{ width: '32px', height: '32px', padding: 0 }}
                          >
                            <i className="bi bi-eye"></i>
                          </Button>

                          {/* Generate DTDC: for In-Progress, Packed, or At Warehouse orders without tracking */}
                          {DTDC_ELIGIBLE_STATUSES.includes((order.status || '').toUpperCase()) && !order.trackingId && (
                            <Button
                              variant="warning"
                              size="sm"
                              className="rounded-circle shadow-sm"
                              onClick={() => handleShipOrder(order)}
                              title="Generate DTDC Consignment & Tracking ID"
                              style={{ width: '32px', height: '32px', padding: 0 }}
                            >
                              <i className="bi bi-truck"></i>
                            </Button>
                          )}

                          {/* Tax Invoice Generation / Preview */}
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="rounded-circle"
                            onClick={() => handlePrintTaxInvoice(order)}
                            title="Generate / View Tax Invoice"
                            style={{ width: '32px', height: '32px', padding: 0 }}
                          >
                            <i className="bi bi-file-earmark-text"></i>
                          </Button>

                          {/* Print Order Slip */}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-circle"
                            onClick={() => handlePrintOrderSlip(order)}
                            title="Print Order Slip"
                            style={{ width: '32px', height: '32px', padding: 0 }}
                          >
                            <i className="bi bi-printer"></i>
                          </Button>

                          {/* DTDC Live Tracking */}
                          {order.trackingId && (
                            <Button
                              variant="outline-dark"
                              size="sm"
                              className="rounded-circle"
                              onClick={() => handleViewTracking(order)}
                              title="Live DTDC Tracking"
                              disabled={fetchingTracking}
                              style={{ width: '32px', height: '32px', padding: 0 }}
                            >
                              <i className="bi bi-geo-alt"></i>
                            </Button>
                          )}

                          {/* DTDC Shipping Label */}
                          {order.trackingId && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="rounded-circle"
                              onClick={() => handlePrintDtdcLabel(order.id)}
                              title="Print DTDC Shipping Label"
                              style={{ width: '32px', height: '32px', padding: 0 }}
                            >
                              <i className="bi bi-tag"></i>
                            </Button>
                          )}

                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="text-center py-5 text-muted">
                    <i className="bi bi-box-seam fs-1 d-block mb-2 text-secondary"></i>
                    {statusFilter === "IN_PROGRESS"
                      ? "No in-progress orders found."
                      : "No DTDC orders found for the selected criteria."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div className="card-footer bg-white border-top py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center border rounded px-2 py-1 bg-light">
              <span className="text-muted small me-2">Show</span>
              <Form.Select
                size="sm"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border-0 bg-transparent fw-bold"
                style={{ width: 'auto' }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
              <span className="text-muted small ms-2">entries</span>
            </div>
            <span className="text-muted small">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} orders
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="rounded-pill px-3 fw-bold"
            >
              <i className="bi bi-chevron-left me-1"></i> Prev
            </Button>
            <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="rounded-pill px-3 fw-bold"
            >
              Next <i className="bi bi-chevron-right ms-1"></i>
            </Button>
          </div>
        </div>
      </div>

      {/* DTDC Tracking History Modal */}
      <TrackingDetailsModal
        show={showTrackingModal}
        onHide={() => setShowTrackingModal(false)}
        trackingData={trackingData}
      />

      {/* Order Details & Dispatch Modal */}
      <Modal
        show={Boolean(selectedViewOrder)}
        onHide={() => setSelectedViewOrder(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white py-2">
          <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-receipt"></i>
            Order Details & Shipment Management #{selectedViewOrder?.orderId || selectedViewOrder?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {selectedViewOrder && (
            <div>
              {/* Order Status & Tracking Summary */}
              <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded mb-3 border">
                <div>
                  <span className="text-muted small d-block">Status</span>
                  <span className="badge bg-primary text-uppercase px-2 py-1">
                    {selectedViewOrder.status || 'PENDING'}
                  </span>
                </div>
                <div>
                  <span className="text-muted small d-block">DTDC Tracking No.</span>
                  {selectedViewOrder.trackingId ? (
                    <span className="fw-bold text-dark font-monospace">{selectedViewOrder.trackingId}</span>
                  ) : (
                    <span className="badge bg-warning text-dark">Not Dispatched</span>
                  )}
                </div>
                <div>
                  <span className="text-muted small d-block">Order Date</span>
                  <span className="fw-semibold">{formatDateTime(selectedViewOrder.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted small d-block">Total Amount</span>
                  <span className="fw-bold text-success fs-6">₹{selectedViewOrder.totalPrice}</span>
                </div>
              </div>

              {/* Customer & Address Details */}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100 bg-white">
                    <h6 className="fw-bold text-dark mb-2 small text-uppercase">
                      <i className="bi bi-person me-1 text-primary"></i>Parent / Customer
                    </h6>
                    <p className="mb-1 fw-semibold small">{selectedViewOrder.parent?.parentName || selectedViewOrder.studentNames || 'Customer'}</p>
                    <p className="mb-1 text-muted small"><i className="bi bi-telephone me-1"></i>{selectedViewOrder.parent?.phoneNumber || '-'}</p>
                    <p className="mb-0 text-muted small"><i className="bi bi-envelope me-1"></i>{selectedViewOrder.parent?.email || '-'}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100 bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold text-dark mb-0 small text-uppercase">
                        <i className="bi bi-geo-alt me-1 text-danger"></i>Delivery Address
                      </h6>
                      {isSuperadmin && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="py-0 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => setShowAddressModal(true)}
                        >
                          <i className="bi bi-pencil-square me-1"></i>Edit
                        </Button>
                      )}
                    </div>
                    <p className="mb-1 small text-dark">{selectedViewOrder.deliveryAddress || selectedViewOrder.billingAddress || 'Campus Pick-up'}</p>
                    <p className="mb-1 small text-muted">
                      {selectedViewOrder.city || selectedViewOrder.parent?.city || "Hyderabad"}, {selectedViewOrder.state || selectedViewOrder.parent?.state || "Telangana"} - {selectedViewOrder.pincode || selectedViewOrder.parent?.pincode || "--"}
                      {selectedViewOrder.isAddressEdited && (
                        <span className="badge bg-info bg-opacity-25 text-primary border ms-2">Edited</span>
                      )}
                    </p>
                    {selectedViewOrder.shippingMethod && (
                      <span className="badge bg-light text-secondary border">Method: {selectedViewOrder.shippingMethod}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <h6 className="fw-bold text-dark mb-2 small text-uppercase">
                <i className="bi bi-box-seam me-1 text-primary"></i>Ordered Uniform Items
              </h6>
              <div className="table-responsive border rounded mb-3">
                <Table hover size="sm" className="mb-0 small align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Item / Bundle</th>
                      <th className="text-center">Size</th>
                      <th className="text-center">QTY</th>
                      <th className="text-end">Rate</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedViewOrder.items && selectedViewOrder.items.length > 0 ? (
                      selectedViewOrder.items.flatMap((item, idx) => {
                        const studentName = item.student?.studentName || item.studentName || '-';
                        const studentUsid = item.student?.usid || item.studentId || '';
                        const student = item.student || {};

                        if (item.orderItemProducts && item.orderItemProducts.length > 0) {
                          return item.orderItemProducts
                            .filter((oip) => Number(oip.totalQuantity || oip.quantity || 1) > 0)
                            .map((oip, oipIdx) => {
                              let productName = oip.product?.name || oip.name || "";

                              if (
                                oip.product?.display_name === "Sports V Collar T-Shirt" ||
                                oip.product?.name ===
                                  "TGS Uni V Collar Slate HD Tee - 4 Colors" ||
                                oip.product?.name === "Sports CC T-Shirt" ||
                                oip.product?.name === "TGS Uni CC Slate Tee - 4 Colors" ||
                                productName.includes("4 Colors")
                              ) {
                                const house = student.house;
                                if (house === "Dragons") {
                                  productName = productName.replace(/4 Colors/g, "Red");
                                } else if (house === "Pegasus") {
                                  productName = productName.replace(/4 Colors/g, "Blue");
                                } else if (house === "Sphinx") {
                                  productName = productName.replace(/4 Colors/g, "Yellow");
                                } else if (house === "Griffins") {
                                  productName = productName.replace(/4 Colors/g, "Green");
                                }
                              }

                              const displayName = oip.product?.display_name
                                ? `${oip.product.display_name} (${productName || oip.product.name})`
                                : productName || oip.product?.name || item.bundle?.name || "Uniform Item";

                              const qty = Number(oip.totalQuantity || oip.quantity || 1);
                              const rate = Number(oip.price || oip.product?.price || (item.unitPrice ? (item.unitPrice / item.orderItemProducts.length) : 0));
                              const amount = rate * qty;

                              return (
                                <tr key={`${idx}-${oipIdx}`}>
                                  <td>{idx + 1}.{oipIdx + 1}</td>
                                  <td>
                                    <div className="fw-semibold">{studentName}</div>
                                    {studentUsid && <span className="text-muted" style={{ fontSize: '11px' }}>USID: {studentUsid}</span>}
                                    {student.house && <span className="badge bg-light text-primary ms-1" style={{ fontSize: '10px' }}>{student.house}</span>}
                                  </td>
                                  <td>
                                    <div className="fw-semibold">{displayName}</div>
                                    {item.bundle?.name && <span className="text-muted" style={{ fontSize: '11px' }}>Bundle: {item.bundle.name}</span>}
                                  </td>
                                  <td className="text-center">{oip.size || '-'}</td>
                                  <td className="text-center fw-bold">{qty}</td>
                                  <td className="text-end">₹{rate.toFixed(2)}</td>
                                  <td className="text-end fw-bold">₹{amount.toFixed(2)}</td>
                                </tr>
                              );
                            });
                        }

                        let bundleName = item.bundle?.name || item.name || 'Uniform Item';
                        const house = student.house;
                        if (house && bundleName.includes("4 Colors")) {
                          if (house === "Dragons") bundleName = bundleName.replace(/4 Colors/g, "Red");
                          else if (house === "Pegasus") bundleName = bundleName.replace(/4 Colors/g, "Blue");
                          else if (house === "Sphinx") bundleName = bundleName.replace(/4 Colors/g, "Yellow");
                          else if (house === "Griffins") bundleName = bundleName.replace(/4 Colors/g, "Green");
                        }

                        return [(
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="fw-semibold">{studentName}</div>
                              {studentUsid && <span className="text-muted" style={{ fontSize: '11px' }}>USID: {studentUsid}</span>}
                              {student.house && <span className="badge bg-light text-primary ms-1" style={{ fontSize: '10px' }}>{student.house}</span>}
                            </td>
                            <td>
                              <div className="fw-semibold">{bundleName}</div>
                            </td>
                            <td className="text-center">{item.size || item.orderItemProducts?.[0]?.size || '-'}</td>
                            <td className="text-center fw-bold">{item.quantity || 1}</td>
                            <td className="text-end">₹{Number(item.unitPrice || item.price || 0).toFixed(2)}</td>
                            <td className="text-end fw-bold">₹{((item.unitPrice || item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                          </tr>
                        )];
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-3 text-muted">No item details available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light d-flex justify-content-between">
          <Button variant="secondary" size="sm" onClick={() => setSelectedViewOrder(null)}>
            Close
          </Button>
          <div className="d-flex gap-2">
            {/* If order is eligible and not shipped yet, allow Dispatch & generate barcode */}
            {!selectedViewOrder?.trackingId && DTDC_ELIGIBLE_STATUSES.includes((selectedViewOrder?.status || '').toUpperCase()) ? (
              <>
                <Button
                  variant="warning"
                  size="sm"
                  className="fw-bold shadow-sm"
                  onClick={() => handleShipOrder(selectedViewOrder)}
                  title="Generate DTDC consignment and tracking barcode"
                >
                  <i className="bi bi-truck me-1"></i> Generate DTDC
                </Button>
              </>
            ) : selectedViewOrder?.trackingId ? (
              <>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => handleViewTracking(selectedViewOrder)}
                >
                  <i className="bi bi-geo-alt me-1"></i> Live Tracking
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePrintDtdcLabel(selectedViewOrder.id)}
                >
                  <i className="bi bi-tag me-1"></i> Shipping Label
                </Button>
              </>
            ) : null}
            {/* Print Slip Button */}
            <Button
              variant="outline-secondary"
              size="sm"
              className="fw-bold px-3"
              onClick={() => handlePrintOrderSlip(selectedViewOrder)}
            >
              <i className="bi bi-printer me-1"></i> Print Order Slip
            </Button>
            {/* Tax Invoice Preview Button */}
            <Button
              variant="success"
              size="sm"
              className="fw-bold px-3"
              onClick={() => handlePrintTaxInvoice(selectedViewOrder)}
            >
              <i className="bi bi-file-earmark-text me-1"></i> Tax Invoice (with Barcode)
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* PDF / Invoice Preview Modal */}
      <Modal
        show={pdfPreview.show}
        onHide={() => setPdfPreview({ ...pdfPreview, show: false })}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title className="fs-6 fw-bold">
            <i className="bi bi-file-earmark-pdf me-2"></i>Document Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '80vh', padding: 0, backgroundColor: '#525659' }}>
          {pdfPreview.url && (
            <iframe
              src={pdfPreview.url}
              width="100%"
              height="100%"
              title="PDF Preview"
              style={{ border: 'none' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" size="sm" onClick={() => setPdfPreview({ ...pdfPreview, show: false })}>
            Close
          </Button>
          <Button variant="primary" size="sm" onClick={handleDownloadPdf} className="px-3 fw-bold">
            <i className="bi bi-download me-1"></i> Download PDF
          </Button>
        </Modal.Footer>
      </Modal>

      {showAddressModal && selectedViewOrder && (
        <UpdateOrderAddressModal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          order={selectedViewOrder}
          onSuccess={(updatedOrder) => {
            setSelectedViewOrder((prev) => ({ ...prev, ...updatedOrder }));
            getShippedOrders();
          }}
        />
      )}

      {/* DTDC Config Manager Modal - Rendered ONLY for Superadmin */}
      {isSuperadmin && (
        <DtdcConfigManager
          show={showConfigManager}
          onHide={() => setShowConfigManager(false)}
          onConfigChange={(list) => {
            setActiveDtdcConfigs(Array.isArray(list) ? list.filter((c) => c.isActive) : []);
          }}
        />
      )}

      {/* DTDC Customer Code Selection Prompt Modal (Single or Bulk) */}
      <Modal
        show={shipPromptModal.show}
        onHide={() => setShipPromptModal({ show: false, order: null, orderIds: [], customerCode: "" })}
        centered
        size="md"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="fs-6 fw-bold">
            <i className="bi bi-truck me-2"></i>Select DTDC Customer Code
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="small text-muted mb-3">
            {shipPromptModal.orderIds && shipPromptModal.orderIds.length > 0 ? (
              <>Choose which DTDC account / customer code to use for booking <strong>{shipPromptModal.orderIds.length}</strong> selected orders:</>
            ) : (
              <>Choose which DTDC account / customer code to use for booking Order <strong>#{shipPromptModal.order?.orderId}</strong>:</>
            )}
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">DTDC Customer Account</Form.Label>
            <Form.Select
              value={shipPromptModal.customerCode}
              onChange={(e) =>
                setShipPromptModal({ ...shipPromptModal, customerCode: e.target.value })
              }
            >
              {activeDtdcConfigs.map((cfg) => (
                <option key={cfg.id} value={cfg.customerCode}>
                  {cfg.customerCode} - {cfg.accountName || "Account"}{" "}
                  {cfg.isDefault ? "(Default)" : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShipPromptModal({ show: false, order: null, orderIds: [], customerCode: "" })}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (shipPromptModal.orderIds && shipPromptModal.orderIds.length > 0) {
                handleBulkGenerateDtdc(shipPromptModal.orderIds, shipPromptModal.customerCode);
              } else {
                handleShipOrder(shipPromptModal.order, shipPromptModal.customerCode);
              }
            }}
          >
            {shipPromptModal.orderIds && shipPromptModal.orderIds.length > 0
              ? `Dispatch ${shipPromptModal.orderIds.length} Shipments`
              : "Dispatch Shipment"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Update Delivery Status Modal */}
      <Modal
        show={showBulkStatusModal}
        onHide={() => setShowBulkStatusModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-6 fw-bold text-dark d-flex align-items-center gap-2">
            <i className="bi bi-pencil-square text-primary"></i>
            Bulk Update Delivery Status ({selectedOrders.length} Orders Selected)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-4">
            <h6 className="fw-bold small text-muted text-uppercase mb-2">
              Selected Orders Summary
            </h6>
            <div className="table-responsive border rounded shadow-sm" style={{ maxHeight: "220px" }}>
              <table className="table table-sm table-hover align-middle mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Order ID</th>
                    <th>Invoice No</th>
                    <th>Parent / Student</th>
                    <th>Current Status</th>
                    <th>Tracking</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrders.map((orderId) => {
                    const order = orders.find((o) => o.id === orderId);
                    return (
                      <tr key={orderId}>
                        <td className="fw-bold font-monospace">{order?.orderId || orderId}</td>
                        <td>{order?.invoiceNo || "-"}</td>
                        <td>{order?.parent?.parentName || order?.studentNames || "-"}</td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {order?.status ? order.status.replace(/_/g, " ") : "In Progress"}
                          </span>
                        </td>
                        <td>
                          {order?.trackingId ? (
                            <span className="badge bg-success-subtle text-success border">
                              {order.trackingId}
                            </span>
                          ) : (
                            <span className="badge bg-secondary-subtle text-muted border">No AWB</span>
                          )}
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0 border-0"
                            onClick={() => handleSelectOrder(orderId)}
                            title="Remove from selection"
                          >
                            <i className="bi bi-x-circle-fill"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-light p-3 rounded border">
            <h6 className="fw-bold mb-3 small text-uppercase text-dark">
              Target Delivery Status
            </h6>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-muted">Select New Status</Form.Label>
              <Form.Select
                value={bulkTargetStatus}
                onChange={(e) => setBulkTargetStatus(e.target.value)}
                className="form-select"
              >
                <option value="">-- Choose New Status --</option>
                {orderStatusList && orderStatusList.length > 0 ? (
                  orderStatusList.map((status) => (
                    <option key={status.name} value={status.name}>
                      {status.displayName || status.name}
                      {status.name === "PACKED" ? " (Auto-generates DTDC)" : ""}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="ARRIVED_AT_WAREHOUSE">Arrived at Warehouse</option>
                    <option value="PACKED">Packed (Auto-generates DTDC Consignment)</option>
                    <option value="SHIPPED">Shipped</option>
                    <option value="IN_TRANSIT">In Transit</option>
                    <option value="OUT_FOR_DELIVERY">Out For Delivery</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="RTO">RTO (Returned)</option>
                  </>
                )}
              </Form.Select>
            </Form.Group>

            {bulkTargetStatus === "PACKED" && (
              <div className="alert alert-info py-2 px-3 small border-info d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-info-circle-fill fs-5 text-info"></i>
                <div>
                  <strong>Automatic DTDC Generation:</strong> Updating orders to <strong>PACKED</strong> automatically triggers DTDC consignment generation and tracking ID creation in the background for paid orders.
                </div>
              </div>
            )}

            <Form.Group>
              <Form.Label className="small fw-bold text-muted">Status Note (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional note applied to all selected orders..."
                value={bulkStatusNote}
                onChange={(e) => setBulkStatusNote(e.target.value)}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" size="sm" onClick={() => setShowBulkStatusModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleBulkStatusUpdate}
            disabled={!bulkTargetStatus}
            className="px-4 fw-bold"
          >
            Update {selectedOrders.length} Order{selectedOrders.length > 1 ? "s" : ""}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DtdcShipmentOrders;
